import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/constants";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-[0_4px_30px_-20px_rgba(10,25,47,0.25)]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-32">
          <Link to="/" className="flex items-center gap-4 group" data-testid="nav-logo-link">
            <div className="w-24 h-24 lg:w-28 lg:h-28 flex items-center justify-center">
              <img
                src={BRAND.logoShield}
                alt={BRAND.name}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif font-semibold text-lg lg:text-xl text-[color:var(--isa-navy)] tracking-tight">
                Insurances Solutions
              </span>
              <span className="font-sans font-bold text-[10px] lg:text-[11px] uppercase tracking-[0.32em] text-[color:var(--isa-gold)] mt-0.5">
                Agency, LLC
              </span>
            </div>
            <span className="sr-only">{BRAND.name}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/"}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link font-sans text-sm font-medium tracking-wide text-[color:var(--isa-navy)] ${
                    isActive ? "active" : ""
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={BRAND.addressMapUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-address-link"
              className="flex items-center gap-2 text-sm font-sans font-medium text-[color:var(--isa-navy)] hover:text-[color:var(--isa-gold)] transition-colors"
            >
              <MapPin className="w-4 h-4" strokeWidth={1.6} />
              <span className="hidden xl:inline">{BRAND.address}</span>
              <span className="xl:hidden">Albany, GA</span>
            </a>
            <span className="h-4 w-px bg-gray-200" aria-hidden="true" />
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="nav-phone-link"
              className="flex items-center gap-2 text-sm font-sans font-medium text-[color:var(--isa-navy)] hover:text-[color:var(--isa-gold)] transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.6} />
              {BRAND.phone}
            </a>
            <Link
              to="/contact?form=quote"
              className="btn-primary"
              data-testid="nav-request-quote-button"
            >
              Request a Quote
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center p-2 text-[color:var(--isa-navy)]"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-gray-100 py-5" data-testid="nav-mobile-menu">
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === "/"}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-2 py-3 font-sans text-sm font-medium rounded-sm ${
                      isActive
                        ? "text-[color:var(--isa-gold)]"
                        : "text-[color:var(--isa-navy)]"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <a
                href={`tel:${BRAND.phoneTel}`}
                className="flex items-center gap-2 px-2 py-3 font-sans text-sm font-medium text-[color:var(--isa-navy)]"
                data-testid="nav-mobile-phone"
              >
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
              <a
                href={BRAND.addressMapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-2 py-3 font-sans text-sm font-medium text-[color:var(--isa-navy)]"
                data-testid="nav-mobile-address"
              >
                <MapPin className="w-4 h-4" /> {BRAND.address}
              </a>
              <Link
                to="/contact?form=quote"
                className="btn-primary mt-2"
                data-testid="nav-mobile-quote-button"
              >
                Request a Quote
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
